/**
 * PersistentEffectTracker — Manages persistent VFX instances that need
 * manual cleanup (orbiters, status effects, auras).
 *
 * Includes auto-cleanup timeout to prevent memory leaks from
 * forgotten persistent effects.
 *
 * Phase H — typed against IShape (substrate-agnostic). The tween system
 * still uses Phaser's scene.tweens.add against the IShape (Phaser shape
 * structurally satisfies IShape; tween mutates .alpha and .scale
 * properties directly).
 */

import type { IShape } from '../../rendering/IRenderer';
import { TweenHost } from '../../rendering/TweenHost';

const MAX_PERSISTENT_AGE_MS = 60_000; // Auto-cleanup after 60s

interface PersistentEntry {
  objects: IShape[];
  /** Pool generation of each object when tracked (plan 16 DisplayPool). */
  gens: number[];
  createdAt: number;
}

/**
 * Pooled shapes are reused: after a tracked shape is released (its tween
 * ended, or removePrimitive faded it) the pool may hand the same object to
 * another effect. Its generation then differs from the one recorded here,
 * and this entry must leave it alone.
 */
const genOf = (obj: IShape): number => (obj as { generation?: number }).generation ?? 0;
const owned = (entry: PersistentEntry, i: number): boolean =>
  !!entry.objects[i] && genOf(entry.objects[i]) === entry.gens[i];

export type CleanupCallback = (obj: IShape) => void;

export class PersistentEffectTracker {
  private effects = new Map<string, PersistentEntry>();

  /** Track spawned objects under an instance ID. */
  public track(instanceId: string, objects: IShape[]): void {
    this.effects.set(instanceId, { objects, gens: objects.map(genOf), createdAt: Date.now() });
  }

  /** Get tracked objects for an instance. */
  public get(instanceId: string): IShape[] | undefined {
    const entry = this.effects.get(instanceId);
    return entry?.objects.filter((_, i) => owned(entry, i));
  }

  /** Remove a specific primitive by index (fade-out before destroy). */
  public removePrimitive(
    instanceId: string,
    index: number,
    _scene: unknown,
    onDestroy: CleanupCallback,
  ): void {
    const entry = this.effects.get(instanceId);
    if (entry && owned(entry, index)) {
      const obj = entry.objects[index];
      // IShape exposes alpha + setScale; the tween mutates alpha and
      // scaleX/scaleY directly (Phaser GameObject + Pixi-side wrapper
      // both support property assignment).
      TweenHost.add(obj, {
        to: { alpha: 0, scaleX: 0.5, scaleY: 0.5 },
        durationMs: 200,
        onComplete: () => onDestroy(obj),
      });
    }
  }

  /** Stop and cleanup an entire persistent effect. */
  public stop(instanceId: string, onDestroy: CleanupCallback): void {
    const entry = this.effects.get(instanceId);
    if (entry) {
      for (let i = 0; i < entry.objects.length; i++) {
        const obj = entry.objects[i];
        if (owned(entry, i) && (obj as any).active) {
          if ((obj as any).stopFollow) (obj as any).stopFollow();
          if ((obj as any).stop) (obj as any).stop();
          onDestroy(obj);
        }
      }
      this.effects.delete(instanceId);
    }
  }

  /** Auto-cleanup stale persistent effects. Call periodically. */
  public sweepStale(onDestroy: CleanupCallback): void {
    const now = Date.now();
    this.effects.forEach((entry, id) => {
      if (now - entry.createdAt > MAX_PERSISTENT_AGE_MS) {
        this.stop(id, onDestroy);
      }
    });
  }

  /** Check if an instance ID contains a substring (for entity override cleanup). */
  public getKeysMatching(substring: string): string[] {
    const result: string[] = [];
    for (const key of this.effects.keys()) {
      if (key.includes(substring)) result.push(key);
    }
    return result;
  }

  public clear(): void {
    this.effects.clear();
  }
}
